import React, { useEffect, useMemo, useState } from "react";
import Calculator from "./global/Calculator";
import ClosePriceInput from "./global/inputs/ClosePriceInput";
import DepositCurrencyInput from "./global/inputs/DepositCurrencyInput";
import InstrumentInput from "./global/inputs/InstrumentInput";
import OpenPriceInput from "./global/inputs/OpenPriceInput";
import PositionDirectionInput from "./global/inputs/PositionDirectionInput";
import PriceInput from "./global/inputs/PriceInput";
import UnitsOf1Lot from "./global/inputs/UnitsOf1Lot";
import {
  getCache,
  getCaches,
  setCaches,
  useElementSize,
} from "../lib/custom-hooks";
import { depositCurrencyMarket, formatResultNum } from "../lib/funcs";
import TradeSizeInput from "./global/inputs/TradeSizeInput";
import { OPERATIONS } from "../lib/constants";

function ProfitCalculator(props: { noLine?: boolean }) {
  const defaultResult = useMemo(() => ({ name: "Profit/Loss", result: "----" }), []);
  const elementSize = useElementSize();
  const [tradeSizeInfo, setTradeSizeInfo] = useState<
    ITradeSizeInfo | undefined
  >(getCache("profit", "tradeSizeInfo", undefined));
  const [switches, setSwitches] = useState(
    getCache("profit", "switches", [
      { label: "Forex", checked: true },
      { label: "Crypto", checked: true },
    ])
  );
  const [instruments, setInstruments] = useState<IInstrument[]>(
    getCache("profit", "instruments", [])
  );
  const [depositCurrencies, setDepositCurrencies] = useState<
    IDepositCurrency[]
  >(getCache("profit", "depositCurrencies", []));
  const [depositableCurrencies, setDepositableCurrencies] = useState<
    IDepositCurrency[]
  >(getCache("profit", "depositableCurrencies", []));
  const [selectedInstrument, setSelectedInstrument] = useState<
    IInstrument | undefined
  >(getCache("profit", "selectedInstrument", undefined));
  const [priceInstrument, setPriceInstrument] = useState<
    (IInstrument & { operation: string }) | undefined
  >(getCache("profit", "priceInstrument", undefined));
  const [selectedDepositCurrency, setSelectedDepositCurrency] = useState<
    IDepositCurrency | undefined
  >(getCache("profit", "selectedDepositCurrency", undefined));
  const [price, setPrice] = useState<number | string>(
    getCache("profit", "price", 1.0)
  );
  const [openPrice, setOpenPrice] = useState<number | string>(
    getCache("profit", "openPrice", 1.0)
  );
  const [closePrice, setClosePrice] = useState<number | string>(
    getCache("profit", "closePrice", 1.01)
  );
  const [selectedPositionDirection, setSelectedPositionDirection] =
    useState<IPositionDirection>(
      getCache("profit", "selectedPositionDirection", {
        value: 1,
        name: "Buy",
        desc: "Long",
      })
    );
  const [result, setResult] = useState<IResult>([defaultResult]);
  const [lotUnits, setLotUnits] = useState<number | string>(
    getCache("profit", "lotUnits", 100000)
  );

  useEffect(() => {
    setCaches("profit", {
      ...getCaches("profit"),
      switches,
      instruments,
      depositCurrencies,
      depositableCurrencies,
      selectedInstrument,
      priceInstrument,
      selectedDepositCurrency,
      price,
      openPrice,
      closePrice,
      selectedPositionDirection,
      lotUnits,
      tradeSizeInfo,
    });
  }, [
    switches,
    instruments,
    depositCurrencies,
    depositableCurrencies,
    selectedInstrument,
    priceInstrument,
    selectedDepositCurrency,
    price,
    openPrice,
    closePrice,
    selectedPositionDirection,
    lotUnits,
    tradeSizeInfo,
  ]);

  useEffect(() => {
    if (!selectedInstrument) return;
    const price = Number(selectedInstrument.SymbolRate.toFixed(6));
    setOpenPrice(price);
    const x = selectedInstrument.PipSize * 200;
    setSelectedPositionDirection(selectedPositionDirection => {
      setClosePrice(
        Number((price + selectedPositionDirection.value * x).toFixed(6))
      );
      return selectedPositionDirection;
    });
  }, [selectedInstrument]);

  useEffect(() => {
    if (
      !selectedInstrument ||
      !selectedDepositCurrency ||
      selectedInstrument.QuoteCurrency ===
        selectedDepositCurrency.GlobalCurrencyName
    ) {
      setPriceInstrument(undefined);
      return;
    }
    if (
      selectedInstrument.BaseCurrency ===
      selectedDepositCurrency.GlobalCurrencyName
    ) {
      setPriceInstrument({ ...selectedInstrument, operation: OPERATIONS.MUL });
      const price = Number(selectedInstrument.SymbolRate.toFixed(6));
      setPrice(price);
      return;
    }
    let pInstrument = selectedInstrument;
    let operation = OPERATIONS.MUL;
    setInstruments(instruments => {
      instruments.forEach((i) => {
        if (
          i.QuoteCurrency === selectedInstrument.QuoteCurrency &&
          i.BaseCurrency === selectedDepositCurrency.GlobalCurrencyName
        ) {
          pInstrument = i;
          operation = OPERATIONS.DIV;
        }
        if (
          i.QuoteCurrency === selectedDepositCurrency.GlobalCurrencyName &&
          i.BaseCurrency === selectedInstrument.QuoteCurrency
        ) {
          pInstrument = i;
          operation = OPERATIONS.MUL;
        }
      });
      if (pInstrument) {
        setPriceInstrument({ ...pInstrument, operation });
        const price = Number(pInstrument.SymbolRate.toFixed(6));
        setPrice(price);
      }
      return instruments;
    });
  }, [selectedInstrument, selectedDepositCurrency]);

  const calculate: React.MouseEventHandler<HTMLInputElement> = (e) => {
    e.preventDefault();
    let result: string = "";
    if (!selectedInstrument)
      return setResult([{ name: "Error", result: "No Instrument Selected" }]);
    if (!selectedDepositCurrency)
      return setResult([
        { name: "Error", result: "No Deposit Currency Selected" },
      ]);
    if (!openPrice)
      return setResult([{ name: "Error", result: "Please Enter Open Price" }]);
    if (!closePrice)
      return setResult([{ name: "Error", result: "Please Enter Close Price" }]);
    if (!tradeSizeInfo?.value || !Number(tradeSizeInfo?.value))
      return setResult([
        {
          name: "Error",
          result: "Trade Size can't be " + (tradeSizeInfo?.value || "empty"),
        },
      ]);
    if (!selectedPositionDirection)
      return setResult([
        { name: "Error", result: "Please Select Position Direction" },
      ]);
    if (!lotUnits || !Number(lotUnits))
      return setResult([
        {
          name: "Error",
          result: "Units of 1 lot can't be " + (lotUnits || "empty"),
        },
      ]);

    const fractionCount =
      depositCurrencyMarket(selectedDepositCurrency, instruments) === "Forex"
        ? 2
        : (price + "").split(".")[0].length + 2;

    let tSize =
      tradeSizeInfo.base === "Units"
        ? tradeSizeInfo.value
        : Number(tradeSizeInfo.value) * Number(lotUnits);

    const r1 =
      Number(tSize) *
      (selectedPositionDirection.name === "Buy"
        ? Number(closePrice) - Number(openPrice)
        : Number(openPrice) - Number(closePrice));
    if (
      selectedInstrument.BaseCurrency ===
      selectedDepositCurrency.GlobalCurrencyName
    ) {
      result =
        formatResultNum(r1 / Number(price), fractionCount) +
        " " +
        selectedDepositCurrency.GlobalCurrencyName;
    } else if (
      selectedInstrument.QuoteCurrency ===
      selectedDepositCurrency.GlobalCurrencyName
    ) {
      result =
        formatResultNum(r1, fractionCount) +
        " " +
        selectedDepositCurrency.GlobalCurrencyName;
    } else {
      if (priceInstrument?.operation === OPERATIONS.MUL) {
        result =
          formatResultNum(r1 * Number(price), fractionCount) +
          " " +
          selectedDepositCurrency.GlobalCurrencyName;
      } else {
        result =
          formatResultNum(r1 / Number(price), fractionCount) +
          " " +
          selectedDepositCurrency.GlobalCurrencyName;
      }
    }
    setResult([{ name: "Profit/Loss", result }]);
  };

  useEffect(() => {
    setResult([defaultResult]);
  }, [
    defaultResult,
    switches,
    selectedInstrument,
    selectedDepositCurrency,
    tradeSizeInfo?.value,
    price,
    openPrice,
    closePrice,
    selectedPositionDirection
  ]);

  useEffect(() => {
    if (!selectedInstrument || !depositCurrencies.length)
      return setDepositableCurrencies([]);
    const filteredInstruments = instruments.filter(
      (ins) =>
        selectedInstrument.QuoteCurrency === ins.BaseCurrency ||
        selectedInstrument.QuoteCurrency === ins.QuoteCurrency
    );
    setDepositableCurrencies(
      depositCurrencies.filter(
        (dc) =>
          filteredInstruments.filter(
            (fi) =>
              fi.BaseCurrency === dc.GlobalCurrencyName ||
              fi.QuoteCurrency === dc.GlobalCurrencyName
          ).length
      )
    );
  }, [depositCurrencies, selectedInstrument, instruments]);

  return (
    <Calculator
      type="profit"
      noLine={props.noLine}
      result={result}
      calculate={calculate}
      setInstruments={setInstruments}
      setSelectedInstrument={setSelectedInstrument}
      setDepositCurrencies={setDepositCurrencies}
      setSelectedDepositCurrency={setSelectedDepositCurrency}
      switches={switches}
      selectedInstrument={selectedInstrument}
      selectedDepositCurrency={selectedDepositCurrency}
      depositableCurrencies={depositableCurrencies}
      setTradeSizeInfo={setTradeSizeInfo}
      setLotUnits={setLotUnits}
    >
      <InstrumentInput
        className={`col-${Math.min(12, 2 * elementSize)}`}
        instruments={instruments}
        switches={switches}
        setSwitches={setSwitches}
        selectedInstrument={selectedInstrument}
        setSelectedInstrument={setSelectedInstrument}
      />

      <DepositCurrencyInput
        className={`col-${Math.max(6, Math.min(12, elementSize))}`}
        depositCurrencies={depositableCurrencies}
        setSelectedDepositCurrency={setSelectedDepositCurrency}
        selectedDepositCurrency={selectedDepositCurrency}
      />

      <PositionDirectionInput
        className={`col-${Math.max(6, Math.min(12, elementSize))}`}
        selectedPositionDirection={selectedPositionDirection}
        setSelectedPositionDirection={setSelectedPositionDirection}
      />

      {tradeSizeInfo ? (
        <TradeSizeInput
          className={`col-${Math.min(12, elementSize * 2)}`}
          lotUnits={lotUnits}
          tradeSizeInfo={tradeSizeInfo}
          setTradeSizeInfo={setTradeSizeInfo}
        />
      ) : null}

      <OpenPriceInput
        className={`col-${elementSize}`}
        openPrice={openPrice}
        setOpenPrice={setOpenPrice}
      />

      <ClosePriceInput
        className={`col-${elementSize}`}
        closePrice={closePrice}
        setClosePrice={setClosePrice}
      />

      <PriceInput
        setSelectedDepositCurrency={setSelectedDepositCurrency}
        setDepositCurrencies={setDepositCurrencies}
        setInstruments={setInstruments}
        className={`col-${elementSize}`}
        priceInstrument={priceInstrument}
        price={price}
        setPrice={setPrice}
      />

      <UnitsOf1Lot
        className={`col-${elementSize}`}
        selectedInstrument={selectedInstrument}
        setLotUnits={setLotUnits}
        lotUnits={lotUnits}
      />
    </Calculator>
  );
}

export default ProfitCalculator;
